import React, { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { getOnboardingHintsState } from '../../api/onboardingHints';
import { HintCondition, HintConfig, hintsConfig } from '../../config/hintsConfig';
import { MINUTES_TO_MS } from '../../config/hintsConsts';
import { hintStorage } from '../../lib/hintStorage';
import { useScrollLock } from '../../lib/useScrollLock';
import { HintBackdrop } from '../HintBackdrop/HintBackdrop';
import { HintTooltip } from '../HintTooltip/HintTooltip';

const SESSION_START = Date.now();

const evaluateCondition = (condition: HintCondition, activeTimeMs: number): boolean => {
    switch (condition.type) {
        case 'page-enter':
            return true;
        case 'element-present':
            return !!document.querySelector(condition.selector);
        case 'hint-done':
            return hintStorage.getHint(condition.hintId).permanent;
        case 'hint-not-shown':
            return hintStorage.getHint(condition.hintId).shown === 0;
        case 'time-on-page':
            return activeTimeMs / MINUTES_TO_MS >= condition.minutes;
        case 'time-after-hint-done': {
            const record = hintStorage.getHint(condition.hintId);
            if (!record.permanent) return false;
            // effectiveStart = max(completedAt, SESSION_START) — сбрасывает таймер при перезагрузке
            const effectiveStart = Math.max(record.completedAt ?? 0, SESSION_START);
            return Date.now() - effectiveStart >= condition.minutes * MINUTES_TO_MS;
        }
        default:
            return false;
    }
};

const hasBlockingOverlay = () =>
    !!document.querySelector(
        '[data-ui-modal="true"], [data-ui-popup="true"], [aria-modal="true"]'
    );

export const HintsOrchestrator = () => {
    const { pathname } = window.location;
    const [dismissed, setDismissed] = useState(false);
    const [activeHint, setActiveHint] = useState<HintConfig | null>(null);
    const [targetEl, setTargetEl] = useState<Element | null>(null);
    const [discoveryKey, setDiscoveryKey] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isEnabled, setIsEnabled] = useState(true);
    const activeTimeRef = useRef(0);
    const lastActiveRef = useRef<number | null>(document.hidden ? null : Date.now());

    const getActiveTimeMs = () => {
        if (lastActiveRef.current === null) return activeTimeRef.current;
        return activeTimeRef.current + (Date.now() - lastActiveRef.current);
    };

    useEffect(() => {
        let isMounted = true;

        getOnboardingHintsState()
            .then(({ completedHints, enabled }) => {
                if (!isMounted) return;

                hintStorage.mergeCompletedHints(completedHints);
                setIsEnabled(enabled);
            })
            .catch((error) => {
                console.warn('Failed to load onboarding hints state', error);
            })
            .finally(() => {
                if (isMounted) {
                    setIsInitialized(true);
                    setDiscoveryKey((k) => k + 1);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    // Track active time; re-trigger discovery when tab regains focus
    useEffect(() => {
        activeTimeRef.current = 0;
        lastActiveRef.current = document.hidden ? null : Date.now();

        const onVisibility = () => {
            if (document.hidden) {
                if (lastActiveRef.current !== null) {
                    activeTimeRef.current += Date.now() - lastActiveRef.current;
                    lastActiveRef.current = null;
                }
            } else {
                lastActiveRef.current = Date.now();
                setDiscoveryKey((k) => k + 1);
            }
        };

        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [pathname]);

    // Discovery: find next hint + schedule timers for time-on-page hints
    useEffect(() => {
        let timers: ReturnType<typeof setTimeout>[] = [];

        const isHintAvailable = (hint: HintConfig) => {
            const record = hintStorage.getHint(hint.id);

            if (record.permanent) return false;
            if (hint.maxShows !== undefined && record.shown >= hint.maxShows) return false;
            if (!document.querySelector(hint.targetSelector)) return false;

            return evaluateCondition(hint.condition, getActiveTimeMs());
        };

        const scheduleTimers = () => {
            timers.forEach(clearTimeout);
            timers = [];
            hintsConfig.forEach((hint) => {
                let remainingMs: number | null = null;

                if (!hint.pagePattern.test(pathname)) {
                    return;
                }
                if (hintStorage.getHint(hint.id).permanent) {
                    return;
                }

                if (hint.condition.type === 'time-on-page') {
                    remainingMs = hint.condition.minutes * MINUTES_TO_MS - getActiveTimeMs();
                } else if (hint.condition.type === 'time-after-hint-done') {
                    const depRecord = hintStorage.getHint(hint.condition.hintId);
                    if (!depRecord.permanent) {
                        return;
                    }
                    const effectiveStart = Math.max(depRecord.completedAt ?? 0, SESSION_START);
                    remainingMs = effectiveStart + hint.condition.minutes * MINUTES_TO_MS - Date.now();
                }

                if (remainingMs === null) {
                    return;
                }
                if (remainingMs <= 0) {
                    setDiscoveryKey((k) => k + 1);
                } else {
                    timers.push(setTimeout(() => setDiscoveryKey((k) => k + 1), remainingMs));
                }
            });
        };

        const check = () => {
            if (!isInitialized || !isEnabled) {
                return;
            }

            if (hasBlockingOverlay()) {
                if (activeHint) {
                    setActiveHint(null);
                    setTargetEl(null);
                }
                return;
            }

            if (activeHint && !dismissed) return;
            if (activeHint && dismissed && isHintAvailable(activeHint)) return;

            const nextHint = hintsConfig.find((hint) => {
                if (!hint.pagePattern.test(pathname)) return false;
                if (!isHintAvailable(hint)) return false;

                return !!document.querySelector(hint.targetSelector);
            });

            if (nextHint) {
                const target = document.querySelector(nextHint.targetSelector);
                if (target) {
                    if (nextHint.scrollTo) target.scrollIntoView({ block: 'center' });
                    setDismissed(false);
                    setActiveHint(nextHint);
                    setTargetEl(target);
                    return;
                }
            }

            if (activeHint) {
                setActiveHint(null);
                setTargetEl(null);
            }

            scheduleTimers();
        };

        check();
        const observer = new MutationObserver(check);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
            timers.forEach(clearTimeout);
        };
    }, [pathname, discoveryKey, activeHint, dismissed, isEnabled, isInitialized]);

    const markedHintIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (activeHint && !dismissed && markedHintIdRef.current !== activeHint.id) {
            markedHintIdRef.current = activeHint.id;
            hintStorage.markShown(activeHint.id);
        }
    }, [activeHint, dismissed]);

    useEffect(() => {
        if (!targetEl || dismissed) return undefined;
        targetEl.setAttribute('data-hint-highlighted', '');

        return () => {
            targetEl.removeAttribute('data-hint-highlighted');
        };
    }, [targetEl, dismissed]);

    useEffect(() => {
        if (!activeHint || dismissed || activeHint.behavior.type !== 'action') return undefined;
        const { actionSelector } = activeHint.behavior;
        if (!actionSelector) return undefined;

        const handler = (e: MouseEvent) => {
            if ((e.target as Element).closest(actionSelector)) {
                if (activeHint.behavior.type === 'action' && activeHint.behavior.permanent) {
                    hintStorage.markCompleted(activeHint.id);
                }
                setDismissed(true);
            }
        };
        document.addEventListener('click', handler);
        return () => {
            document.removeEventListener('click', handler);
        };
    }, [activeHint, dismissed]);

    const isActive = !!activeHint && !!targetEl && !dismissed;
    useScrollLock(isActive);

    const handleDismiss = () => setDismissed(true);

    const handleAction = () => {
        if (!activeHint) return;

        if (activeHint.behavior.type === 'action') {
            if (activeHint.behavior.permanent) {
                hintStorage.markCompleted(activeHint.id);
            }
            if (activeHint.behavior.actionSelector) {
                const el = document.querySelector(
                    activeHint.behavior.actionSelector
                ) as HTMLElement | null;
                el?.click();
            }
            setDismissed(true);
            return;
        }

        if (activeHint.behavior.type === 'ok') {
            hintStorage.markCompleted(activeHint.id);
            setActiveHint(null);
            setTargetEl(null);
            setDiscoveryKey((k) => k + 1);
            return;
        }

        if (activeHint.behavior.type === 'next') {
            hintStorage.markCompleted(activeHint.id);
            if (activeHint.behavior.actionSelector) {
                const actionEl = document.querySelector(
                    activeHint.behavior.actionSelector
                ) as HTMLElement | null;
                actionEl?.click();
            }
            const nextHint = hintsConfig.find((h) => h.id === activeHint.behavior.nextHintId);
            if (nextHint) {
                if (nextHint.prepareSelector) {
                    const prepareEl = document.querySelector(
                        nextHint.prepareSelector
                    ) as HTMLElement | null;
                    prepareEl?.click();
                    setActiveHint(null);
                    setTargetEl(null);
                    setDiscoveryKey((k) => k + 1);
                    return;
                }
                const nextTarget = document.querySelector(nextHint.targetSelector);
                if (nextTarget) {
                    if (nextHint.scrollTo) nextTarget.scrollIntoView({ block: 'center' });
                    setActiveHint(nextHint);
                    setTargetEl(nextTarget);
                    return;
                }
            }
            setDismissed(true);
        }
    };

    if (!isInitialized || !isEnabled || !activeHint || !targetEl || dismissed) return null;

    return (
        <>
            <HintBackdrop targetEl={targetEl} backdropOptions={activeHint.backdropOptions} />
            {createPortal(
                <HintTooltip
                    text={activeHint.text}
                    behavior={activeHint.behavior}
                    targetEl={targetEl}
                    position={activeHint.tooltipPosition}
                    onDismiss={handleDismiss}
                    onAction={handleAction}
                    backdropOptions={activeHint.backdropOptions}
                />,
                document.body
            )}
        </>
    );
};
